import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { formatIDR, formatNumber } from '@/lib/format';
import { IronDivider, FieldLabel } from '@/components/ui/primitives';

const QUICK_AMOUNTS = [50_000, 100_000, 250_000, 500_000] as const;

interface BidFormProps {
  currentHighest: number;
  minNextBid: number;
  myUsername: string;
  onBid: (amount: number) => Promise<void>;
  mode: 'authenticated' | 'guest' | 'loading';
  submitState: 'idle' | 'pending' | 'success' | 'error';
  errorMessage?: string;
  redirectTo?: string;
}

export function BidForm({
  currentHighest,
  minNextBid,
  myUsername,
  onBid,
  mode,
  submitState,
  errorMessage,
  redirectTo,
}: BidFormProps) {
  const [rawInput, setRawInput] = useState('');
  const [validationMessage, setValidationMessage] = useState('');

  const parsedAmount = parseInt(rawInput, 10) || 0;
  const displayValue = rawInput ? formatNumber(parsedAmount) : '';
  const isSubmitting = submitState === 'pending';
  const isBlocked = mode !== 'authenticated';
  const authSearch = redirectTo ? { redirect: redirectTo } : {};

  useEffect(() => {
    if (submitState === 'success') {
      setRawInput('');
      setValidationMessage('');
    }
  }, [submitState]);

  const addQuick = useCallback(
    (amount: number) => {
      if (isBlocked || isSubmitting) return;

      const base = parsedAmount || minNextBid;
      setRawInput(String(base + amount));
      setValidationMessage('');
    },
    [isBlocked, isSubmitting, minNextBid, parsedAmount],
  );

  const submit = useCallback(() => {
    if (isBlocked || isSubmitting) return;

    if (!parsedAmount) {
      setValidationMessage('Please enter a valid bid amount.');
      return;
    }

    if (parsedAmount < minNextBid) {
      setValidationMessage(`Bid must be at least ${formatIDR(minNextBid)}.`);
      return;
    }

    setValidationMessage('');
    void onBid(parsedAmount);
  }, [isBlocked, isSubmitting, minNextBid, onBid, parsedAmount]);

  return (
    <div className="bg-iron-900 border border-iron-800 p-8 space-y-6">
      <div>
        <FieldLabel className="mb-2">Current Highest Bid</FieldLabel>
        <p className="font-epic text-secondary text-4xl font-bold tracking-wide">
          {formatIDR(currentHighest)}
        </p>
        <p className="text-[10px] tracking-widest text-iron-500 mt-1 uppercase font-bold">
          Min next bid: {formatIDR(minNextBid)}
        </p>
      </div>

      <IronDivider />

      {mode === 'guest' ? (
        <div className="border border-secondary/30 bg-secondary/10 px-4 py-4 space-y-4">
          <p className="text-xs text-iron-200 leading-relaxed">
            Sign in to place bids, follow your standing, and join the live auction.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/sign-in"
              search={authSearch}
              className="inline-flex items-center justify-center px-4 h-10 bg-secondary text-secondary-foreground font-bold tracking-[0.2em] uppercase text-[10px] hover:bg-secondary/90 transition-colors"
            >
              Sign In To Bid
            </Link>
            <Link
              to="/register"
              search={authSearch}
              className="inline-flex items-center justify-center px-4 h-10 border border-iron-700 text-iron-200 font-bold tracking-[0.2em] uppercase text-[10px] hover:border-secondary/40 hover:text-secondary transition-colors"
            >
              Create Account
            </Link>
          </div>
        </div>
      ) : null}

      {mode === 'loading' ? (
        <div className="border border-iron-800 bg-iron-950/60 px-4 py-4">
          <p className="text-[10px] tracking-[0.25em] uppercase text-iron-500 font-bold">
            Checking your bidding access...
          </p>
        </div>
      ) : null}

      <div>
        <FieldLabel className="mb-3">Quick Add</FieldLabel>
        <div className="flex flex-wrap gap-2">
          {QUICK_AMOUNTS.map((amt) => (
            <button
              key={amt}
              type="button"
              onClick={() => addQuick(amt)}
              disabled={isBlocked || isSubmitting}
              className="border border-iron-700 text-iron-300 text-[10px] tracking-[0.2em] uppercase font-bold px-4 py-2 hover:border-secondary/50 hover:text-secondary transition-colors active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
            >
              +{formatNumber(amt)}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2 group">
        <label
          htmlFor="bid-amount-input"
          className="text-[9px] tracking-[0.35em] uppercase text-iron-400 font-bold block group-focus-within:text-secondary transition-colors"
        >
          Your Bid Amount (IDR)
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-iron-500 text-sm font-bold font-epic">
            Rp
          </span>
          <input
            id="bid-amount-input"
            type="text"
            inputMode="numeric"
            value={displayValue}
            placeholder="0"
            disabled={isBlocked || isSubmitting}
            onChange={(e) => {
              setRawInput(e.target.value.replace(/\D/g, ''));
              setValidationMessage('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
            }}
            className="w-full bg-iron-950 border border-iron-700 focus:border-secondary outline-none py-4 pl-12 pr-4 text-iron-100 font-epic text-lg tracking-wide placeholder:text-iron-700 transition-colors disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>

        {validationMessage ? (
          <p className="text-[10px] tracking-widest text-primary font-bold uppercase">
            {validationMessage}
          </p>
        ) : null}
        {submitState === 'error' && errorMessage ? (
          <p className="text-[10px] tracking-widest text-primary font-bold uppercase">
            {errorMessage}
          </p>
        ) : null}
        {submitState === 'success' ? (
          <p className="text-[10px] tracking-widest text-secondary font-bold uppercase animate-pulse">
            Your bid was placed successfully.
          </p>
        ) : null}
      </div>

      <button
        id="place-bid-btn"
        type="button"
        onClick={submit}
        disabled={isBlocked || isSubmitting}
        className="w-full bg-primary text-white font-epic tracking-[0.3em] uppercase text-sm h-14 shadow-[0_0_20px_rgba(205,1,1,0.25)] hover:shadow-primary/50 hover:brightness-110 active:scale-[0.98] transition-all disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isSubmitting ? 'Placing Bid...' : 'Place Bid'}
      </button>

      <p className="text-[9px] tracking-widest text-iron-600 text-center uppercase">
        {mode === 'authenticated' ? (
          <>
            Bidding as <span className="text-iron-400 font-bold">{myUsername}</span>
          </>
        ) : mode === 'loading' ? (
          'Verifying bidding privileges'
        ) : (
          'Guests can browse, but bidding requires an account'
        )}
      </p>
    </div>
  );
}
